"use client";

import { sendGTMEvent } from "@next/third-parties/google";

import { RootProduct } from "@/lib/centra/formatters";
import { CartItemMaybeHooked } from "@/lib/hooks/useItems";
import { ProductCardType } from "@/types/product";
import { CentraVoucher } from "@frend-digital/centra-legacy/schemas/selection/utilitySchemas";

export const pageview = (url: string) => {
  sendGTMEvent({
    config: `${process.env.NEXT_PUBLIC_GTM_ID}`,
    page_path: url,
  });
};

interface GTagEvent {
  action: string;
  category: string;
  label: string;
  value: number;
}

export const event = ({ action, category, label, value }: GTagEvent) => {
  sendGTMEvent({
    event: action,
    event_category: category,
    event_label: label,
    revenue: value,
  });
};

export const generateGTagItem = (
  item: Awaited<RootProduct>,
  price?: number,
  quantity: number = 1
) => {
  const item_id = item?.displayItemId || "";
  const item_name = item?.name || "";
  const item_category = item.categories?.length
    ? item.categories[item.categories.length - 1].name?.join(" ") || ""
    : "";
  const item_variant = "";
  const item_price = price || NaN;
  const item_quantity = quantity;

  return {
    item_id,
    item_name,
    item_category,
    item_variant,
    price: item_price,
    quantity: item_quantity,
  };
};

export const convertPCToGtagItem = (
  item: ProductCardType,
  price?: number,
  quantity: number = 1
) => {
  const item_id = "";
  const item_name = item?.core?.name || "";
  const item_category = "";
  const item_variant = "";
  const item_price = price || NaN;
  const item_quantity = quantity;

  return {
    item_id,
    item_name,
    item_category,
    item_variant,
    price: item_price,
    quantity: item_quantity,
  };
};

export const convertCartToGTagItem = (item: CartItemMaybeHooked) => {
  const item_id = item?.id || "";
  const item_name = item?.name || "";
  const item_category = "";
  const item_variant = item?.variant || "";
  const item_price = item.priceAsNumber || NaN;
  const item_quantity = item.quantity;

  return {
    item_id,
    item_name,
    item_category,
    item_variant,
    price: item_price,
    quantity: item_quantity,
  };
};

export interface GTagItem {
  item_id: string; // SKU
  item_name: string; // Product Name
  discount?: number;
  item_category: string; // Top Category /women
  item_category2?: string; // Sub Category /women/!dresses!
  item_category3?: string;
  item_variant: string; // Variant name
  price: number; // Price
  quantity?: number; // How many
}
interface EventCartProps {
  currency: string;
  value?: number;
  items: GTagItem[] | [];
}

export const eventAddToCart = ({ currency, value, items }: EventCartProps) => {
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent({
    event: "add_to_cart",
    ecommerce: {
      currencyCode: currency,
      revenue: value,
      products: items,
    },
  });
};

export const eventProductDetail = ({
  currency,
  items,
  value,
}: EventCartProps) => {
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent({
    event: "product_detail",
    ecommerce: {
      currency,
      value,
      items,
    },
  });
};

export const eventViewCart = ({ currency, value, items }: EventCartProps) => {
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent({
    event: "view_cart",
    ecommerce: {
      currencyCode: currency,
      revenue: value,
      products: items,
    },
  });
};

export const eventBeginCheckout = ({
  currency,
  value,
  items,
}: EventCartProps) => {
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent({
    event: "begin_checkout",
    ecommerce: {
      currencyCode: currency,
      revenue: value,
      products: items,
    },
  });
};

interface EventPurchaseProps {
  transaction_id: string;
  tax: number;
  shipping: number;
  coupon: string | CentraVoucher;
  currency: string;
  value: number;
  items: GTagItem[];
}

export const eventPurchase = ({
  transaction_id,
  tax,
  shipping,
  coupon,
  currency,
  value,
  items,
}: EventPurchaseProps) => {
  sendGTMEvent({
    event: "purchase",
    ecommerce: {
      affiliation: "Online Store",
      id: transaction_id,
      tax,
      shipping,
      coupon: typeof coupon === "string" ? coupon : coupon?.voucher || "",
      currency: currency,
      value: value,
      items,
    },
  });
};
